
import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import Icon from './Icon';

const AboutUs: React.FC = () => {
    const { t } = useLanguage();

    const features = [
        {
            icon: 'sparkles',
            title: t('aboutFeature1Title'),
            desc: t('aboutFeature1Desc')
        },
        {
            icon: 'diamond',
            title: t('aboutFeature2Title'),
            desc: t('aboutFeature2Desc')
        },
        {
            icon: 'check',
            title: t('aboutFeature3Title'),
            desc: t('aboutFeature3Desc')
        }
    ];

    return (
        <main className="w-full max-w-4xl mx-auto mt-6 md:mt-12 animate-in fade-in slide-in-from-bottom-4 duration-700 space-y-12 pb-24">
            {/* Hero Section */}
            <div className="text-center space-y-6">
                <div className="w-20 h-20 rounded-full bg-apple-gold/10 flex items-center justify-center mx-auto mb-8 border border-apple-gold/20 shadow-xl shadow-apple-gold/5">
                    <Icon name="diamond" className="w-10 h-10 text-apple-gold" />
                </div>
                <h1 className="text-4xl md:text-6xl font-bold text-slate-900 dark:text-white tracking-tighter leading-tight">
                    {t('aboutHeroTitle')}
                </h1>
                <p className="text-lg md:text-xl text-slate-500 dark:text-slate-400 font-light leading-relaxed max-w-2xl mx-auto">
                    {t('aboutHeroSubtitle')}
                </p>
            </div>

            {/* Bento Style Content */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {features.map((feature, i) => (
                    <div key={i} className="bg-slate-50 dark:bg-apple-black apple-blur rounded-apple p-8 border border-black/5 dark:border-white/5 shadow-sm hover:shadow-md transition-all group">
                        <div className="w-12 h-12 rounded-2xl bg-white dark:bg-white/5 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-sm">
                            <Icon name={feature.icon} className="w-6 h-6 text-apple-gold" />
                        </div>
                        <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-3">{feature.title}</h3>
                        <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed font-medium">
                            {feature.desc}
                        </p>
                    </div>
                ))}
            </div>

            {/* In-depth details */}
            <div className="bg-slate-50 dark:bg-apple-black apple-blur rounded-apple p-10 md:p-16 border border-black/5 dark:border-white/5 space-y-12">
                <section className="space-y-6">
                    <h2 className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight">{t('aboutFunctionalityTitle')}</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                        <div className="space-y-4">
                            <div className="flex items-center gap-3">
                                <span className="w-8 h-8 rounded-full bg-apple-gold text-white flex items-center justify-center text-xs font-black">01</span>
                                <h4 className="font-bold text-lg text-slate-800 dark:text-white">{t('aboutStep1Title')}</h4>
                            </div>
                            <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed pl-11">
                                {t('aboutStep1Desc')}
                            </p>
                        </div>
                        <div className="space-y-4">
                            <div className="flex items-center gap-3">
                                <span className="w-8 h-8 rounded-full bg-apple-gold text-white flex items-center justify-center text-xs font-black">02</span>
                                <h4 className="font-bold text-lg text-slate-800 dark:text-white">{t('aboutStep2Title')}</h4>
                            </div>
                            <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed pl-11">
                                {t('aboutStep2Desc')}
                            </p>
                        </div>
                        <div className="space-y-4">
                            <div className="flex items-center gap-3">
                                <span className="w-8 h-8 rounded-full bg-apple-gold text-white flex items-center justify-center text-xs font-black">03</span>
                                <h4 className="font-bold text-lg text-slate-800 dark:text-white">{t('aboutStep3Title')}</h4>
                            </div>
                            <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed pl-11">
                                {t('aboutStep3Desc')}
                            </p>
                        </div>
                        <div className="space-y-4">
                            <div className="flex items-center gap-3">
                                <span className="w-8 h-8 rounded-full bg-apple-gold text-white flex items-center justify-center text-xs font-black">04</span>
                                <h4 className="font-bold text-lg text-slate-800 dark:text-white">{t('aboutStep4Title')}</h4>
                            </div>
                            <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed pl-11">
                                {t('aboutStep4Desc')}
                            </p>
                        </div>
                    </div>
                </section>
            </div>
        </main>
    );
};

export default AboutUs;
